/**
 * SUB-USERS SU2a -- public acceptance of a team invitation (18-Oct-2026). Route /team/accept?code=<code>, PublicLayout,
 * RequireGuest. Mirrors RegisterPage: the code is verified first (generic negative = one message, no oracle), then a
 * short form (name, locked e-mail, phone, password) with the SAME field rules as /register (registerRequestSchema.in.shape
 * from @sendsms/shared -- one source of truth), submitted to POST /api/auth/team-invite/accept. No session is created:
 * on success the member is sent to /login and enters the existing MFA setup flow. Design: docs/sub-users-su2a-design.md.
 */
import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Eye, EyeOff, Lock, Users } from "lucide-react";
import { z } from "zod";

import { registerRequestSchema, getDialPrefix } from "@sendsms/shared";
import { api } from "@/lib/api";
import { applyFieldErrors, translateApiError, translateFieldError } from "@/lib/form-errors";
import { Card, CardHeader, CardTitle, CardSubtitle, CardBody, CardFooter } from "@/components/ui/Card";
import { Input } from "@/components/ui/Input";
import { Button } from "@/components/ui/Button";
import { Alert } from "@/components/ui/Alert";

/** Same promotion as RegisterPage (national "0722..." -> E.164 digits); the member has no country field, RO is the platform default. */
function normalizePhone(raw: string): string {
  let v = raw.trim().replace(/[\s\-()]/g, "");
  if (v.startsWith("+")) v = v.slice(1);
  if (v.startsWith("00")) return v.slice(2);
  if (v.startsWith("0")) return (getDialPrefix("RO") ?? "") + v.slice(1);
  return v;
}

const FIELDS = registerRequestSchema.in.shape;
/** Exactly the server's AcceptInput: same primitives, no role, no organization (V605). */
const acceptSchema = z
  .object({
    full_name: FIELDS.full_name,
    password: FIELDS.password,
    phone: FIELDS.phone,
  })
  .strict();
type AcceptFormValues = z.input<typeof acceptSchema>;

type VerifyResult = { valid: boolean; email?: string; full_name?: string | null; organization_name?: string; owner_name?: string };

export default function TeamAcceptPage() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const code = (params.get("code") ?? "").trim().toUpperCase();

  const verify = useQuery<VerifyResult>({
    queryKey: ["team-invite-verify", code],
    queryFn: () => api.teamInviteVerify({ code }),
    enabled: code.length > 0,
    retry: false,
    staleTime: 60_000,
  });

  if (code.length === 0 || verify.isError || (verify.data && !verify.data.valid)) {
    return <InvalidCard />;
  }
  if (!verify.data) {
    return (
      <Card>
        <CardBody>
          <p className="text-body text-ink-muted">{t("auth.team_accept.loading")}</p>
        </CardBody>
      </Card>
    );
  }
  return <AcceptForm code={code} invite={verify.data} />;
}

function InvalidCard() {
  const { t } = useTranslation();
  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("auth.team_accept.invalid_title")}</CardTitle>
      </CardHeader>
      <CardBody>
        <Alert tone="danger" description={t("auth.team_accept.invalid")} />
      </CardBody>
      <CardFooter>
        <p className="text-caption text-center text-ink-muted">
          {t("auth.team_accept.have_account")}{" "}
          <Link to="/login" className="font-medium text-brand-500 hover:underline">
            {t("auth.team_accept.sign_in")}
          </Link>
        </p>
      </CardFooter>
    </Card>
  );
}

function AcceptForm({ code, invite }: { code: string; invite: VerifyResult }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [topError, setTopError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const email = invite.email ?? "";

  const form = useForm<AcceptFormValues>({
    resolver: zodResolver(acceptSchema),
    defaultValues: { full_name: invite.full_name ?? "", password: "", phone: "" },
    mode: "onSubmit",
  });

  const mutation = useMutation({
    mutationFn: (body: { code: string; email: string; password: string; full_name: string; phone: string; tz: string }) => api.teamInviteAccept(body),
    onSuccess: () => setDone(true),
    onError: (err: unknown) => {
      const handled = applyFieldErrors(t, err, form.setError);
      if (!handled) setTopError(translateApiError(t, err));
    },
  });

  const err = (name: keyof AcceptFormValues): string | undefined => {
    const e = form.formState.errors[name];
    if (!e?.message) return undefined;
    return translateFieldError(t, String(e.message));
  };

  if (done) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t("auth.team_accept.success_title")}</CardTitle>
          <CardSubtitle>{t("auth.team_accept.success_text")}</CardSubtitle>
        </CardHeader>
        <CardBody>
          <Button type="button" fullWidth size="lg" onClick={() => navigate("/login", { replace: true })}>
            {t("auth.team_accept.go_login")}
          </Button>
        </CardBody>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("auth.team_accept.title")}</CardTitle>
        <CardSubtitle>{t("auth.team_accept.subtitle")}</CardSubtitle>
      </CardHeader>

      <CardBody>
        <div className="mb-5 flex items-center gap-2 rounded-md bg-surface-subtle px-3 py-2.5">
          <Users className="h-4 w-4 flex-shrink-0 text-brand-500" />
          <span className="text-caption text-ink-muted">
            {t("auth.team_accept.invited_by", { owner: invite.owner_name ?? "", org: invite.organization_name ?? "" })}
          </span>
        </div>

        {topError ? <Alert tone="danger" description={topError} className="mb-5" /> : null}

        <form
          onSubmit={form.handleSubmit((data) => {
            setTopError(null);
            mutation.mutate({
              code,
              email,
              password: data.password,
              full_name: data.full_name,
              phone: normalizePhone(String(data.phone ?? "")),
              tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
            });
          })}
          className="space-y-4"
          noValidate
        >
          <Input
            label={t("auth.register.full_name_label")}
            placeholder={t("auth.register.full_name_placeholder")}
            autoComplete="name"
            autoFocus
            required
            {...form.register("full_name")}
            error={err("full_name")}
          />

          <Input
            label={t("auth.register.email_label")}
            type="email"
            value={email}
            readOnly
            disabled
            hint={t("auth.team_accept.email_locked")}
            trailingElement={<Lock className="h-4 w-4 text-ink-subtle" />}
          />

          <Input
            label={t("auth.register.phone_label")}
            type="tel"
            placeholder="+40722000000"
            autoComplete="tel"
            required
            hint={t("auth.team_accept.phone_help")}
            {...form.register("phone", { setValueAs: (v) => normalizePhone(String(v ?? "")) })}
            error={err("phone")}
          />

          <Input
            label={t("auth.register.password_label")}
            type={showPassword ? "text" : "password"}
            autoComplete="new-password"
            required
            hint={t("auth.team_accept.password_help")}
            {...form.register("password")}
            error={err("password")}
            trailingElement={
              <button
                type="button"
                onClick={() => setShowPassword((s) => !s)}
                aria-label={showPassword ? t("common.hide") : t("common.show")}
                className="flex h-8 w-8 items-center justify-center rounded-md text-ink-subtle transition-colors hover:bg-surface-muted focus:outline-none focus-visible:shadow-focus"
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            }
          />

          <Button type="submit" fullWidth size="lg" isLoading={mutation.isPending}>
            {t("auth.team_accept.submit")}
          </Button>
        </form>
      </CardBody>

      <CardFooter>
        <p className="text-caption text-center text-ink-muted">
          {t("auth.team_accept.have_account")}{" "}
          <Link to="/login" className="font-medium text-brand-500 hover:underline">
            {t("auth.team_accept.sign_in")}
          </Link>
        </p>
      </CardFooter>
    </Card>
  );
}